import type React from "react"
import { useState, useEffect, useRef } from "react"
import { useDoctorsBySearch } from "../../lib/hooks/doctors/use-doctors-by-search"
import DoctorCard from "./doctor-card"
import { cn } from "../../lib/utils"
import { getSubdistricts } from "../../services/doctors"

// Define prop types
interface SearchFormProps {
  selectedSpecialty: string | null
  onSearch: ({
    doctorName,
    location,
    doctors,
    totalItems,
    totalPages,
    isLoading,
    isError,
    currentPage,
    setCurrentPage,
  }: {
    doctorName: string
    location: string
    doctors: any[]
    totalItems: number
    totalPages: number
    isLoading: boolean
    isError: boolean
    currentPage: number
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  }) => void
}

const SearchForm = ({ selectedSpecialty, onSearch }: SearchFormProps) => {
  const [doctorName, setDoctorName] = useState("")
  const [location, setLocation] = useState("")
  const [error, setError] = useState("")
  const [doctorNameError, setDoctorNameError] = useState("")
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [subdistricts, setSubdistricts] = useState<string[]>([]);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [committedSearchParams, setCommittedSearchParams] = useState<{
    searchCriteria1: string | null
    searchCriteria2: string | null
    searchCriteria3: string | null
    page: number
    size: number
    sort: string
    enabled: boolean
  }>({
    searchCriteria1: null,
    searchCriteria2: null,
    searchCriteria3: null,
    page: 0,
    size: 10,
    sort: "fullName,asc",
    enabled: false,
  })

  const doctorsPerPage: number = 10
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Call hook with committed search parameters
  const { doctors, totalItems, totalPages, isLoading, isError } = useDoctorsBySearch(committedSearchParams)
  const handleSearch = () => {
    if (!doctorName && !location) {
      setError("Please enter either a doctor's name or location")
      return
    }
    if (doctorNameError) {
      setError("Please fix the doctor name error before searching")
      return
    }
    setError("")

    // Prepare search parameters
    const newSearchParams = {
      searchCriteria1: doctorName || location ? selectedSpecialty : null,
      searchCriteria2: doctorName || location ? doctorName.replace(/^dr\s*/i, "") || null : null,
      searchCriteria3: doctorName || location ? location || null : null,
      page: 0,
      size: doctorsPerPage,
      sort: "fullName,asc",
      enabled: !!(doctorName || location),
    }

    setCommittedSearchParams(newSearchParams)
    setCurrentPage(0)

    // Call onSearch with the new parameters
    onSearch({
      doctorName,
      location,
      doctors,
      totalItems,
      totalPages,
      isLoading,
      isError,
      currentPage: 0,
      setCurrentPage,
    })
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage)
      setCommittedSearchParams((prev) => ({
        ...prev,
        page: newPage,
      }))
      // Update parent state on page change
      onSearch({
        doctorName,
        location,
        doctors,
        totalItems,
        totalPages,
        isLoading,
        isError,
        currentPage: newPage,
        setCurrentPage,
      })
    }
  }

  // // Generate page numbers for pagination
  // const generatePageNumbers = () => {
  //   const pages = [];
  //   const maxVisible = 5; // Show max 5 page numbers at a time

  //   if (totalPages <= maxVisible) {
  //     // Show all pages if total pages is less than or equal to maxVisible
  //     for (let i = 0; i < totalPages; i++) {
  //       pages.push(i);
  //     }
  //   } else {
  //     // Show pages with ellipsis logic
  //     const halfVisible = Math.floor(maxVisible / 2);
  //     let startPage = Math.max(0, currentPage - halfVisible);
  //     let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  //     // Adjust if we're near the beginning or end
  //     if (currentPage < halfVisible) {
  //       endPage = Math.min(totalPages - 1, maxVisible - 1);
  //       startPage = 0;
  //     } else if (currentPage > totalPages - halfVisible - 1) {
  //       startPage = Math.max(0, totalPages - maxVisible);
  //       endPage = totalPages - 1;
  //     }

  //     // Add first page and ellipsis if needed
  //     if (startPage > 0) {
  //       pages.push(0);
  //       if (startPage > 1) {
  //         pages.push(-1); // -1 represents ellipsis
  //       }
  //     }

  //     // Add visible pages
  //     for (let i = startPage; i <= endPage; i++) {
  //       pages.push(i);
  //     }

  //     // Add ellipsis and last page if needed
  //     if (endPage < totalPages - 1) {
  //       if (endPage < totalPages - 2) {
  //         pages.push(-1); // -1 represents ellipsis
  //       }
  //       pages.push(totalPages - 1);
  //     }
  //   }

  //   return pages;
  // };

  useEffect(() => {
    async function fetchSubdistricts() {
      if (!selectedSpecialty) return; // Avoid calling API with null
      try {
        const data = await getSubdistricts(selectedSpecialty);
        setSubdistricts(data);
      } catch (error) {
        console.error("Error fetching subdistricts:", error);
      }
    }
    fetchSubdistricts();
  }, [selectedSpecialty]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Add a ref to your dropdown container and check if click is outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLocationDropdownOpen(false);
      }
    };

    if (isLocationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLocationDropdownOpen]);

  const filteredSubdistricts = subdistricts.filter(subdistrict =>
    subdistrict.trim() !== "" &&
    subdistrict.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  // Calculate the range of doctors being shown
  const startCount = committedSearchParams.enabled && doctors.length > 0 ? currentPage * doctorsPerPage + 1 : 0
  const endCount = committedSearchParams.enabled && doctors.length > 0 ? startCount + doctors.length - 1 : 0

  return (
    <div className="container mx-auto max-w-2xl md:max-w-full md:px-4 pt-4 md:pt-2">
      <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-4">

        <div className="relative md:col-span-1">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">📍</span>

          {/* Dropdown Button */}
          <button
            type="button"
            // onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
            onClick={() => {
              if (isLocationDropdownOpen) {
                setIsLocationDropdownOpen(false);
              } else {
                setIsLocationDropdownOpen(true);
              }
            }}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F676C] bg-white text-gray-800 text-left flex items-center justify-between"
          >
            <span className={location ? "text-gray-800" : "text-gray-500"}>
              {location || "Select a city/subdistrict"}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${isLocationDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isLocationDropdownOpen && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg" ref={dropdownRef}>
              {/* Search Input inside dropdown */}
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search city/subdistricts..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#0F676C] text-sm"
                  value={locationSearchTerm}
                  onChange={(e) => setLocationSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Dropdown Options */}
              <div className="max-h-48 overflow-y-auto">
                {filteredSubdistricts.length > 0 ? (
                  filteredSubdistricts.map((subdistrict) => (
                    <button
                      key={subdistrict}
                      type="button"
                      onClick={() => {
                        setLocation(subdistrict);
                        setLocationSearchTerm("");
                        setIsLocationDropdownOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                    >
                      {subdistrict}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-sm">
                    No subdistricts found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="relative md:col-span-1">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">🔍</span>
          <input
            type="text"
            placeholder="Doctor name"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F676C] bg-white"
            value={doctorName}
            onChange={(e) => {
              const input = e.target.value
              setDoctorName(input)
              // Validate length of the name after stripping "Dr"
              const cleanedInput = input.replace(/^dr\s*/i, "")
              if (cleanedInput.length > 0 && cleanedInput.length < 3) {
                setDoctorNameError("Enter at least 3 characters for the doctor's name (excluding 'Dr')")
              } else {
                setDoctorNameError("")
              }
            }}
          />
        </div>
        {/* Error messages - shown above search button on mobile */}
        <div className="max-md:order-last max-md:col-span-2 md:col-span-3">
          {error && <p className="text-red-500 text-xs sm:text-sm mb-2">{error}</p>}
          {doctorNameError && <p className="text-red-500 text-xs sm:text-sm">{doctorNameError}</p>}
        </div>

        <div className="max-md:flex max-md:justify-end md:col-span-1">
          <button
            onClick={handleSearch}
            className="w-24 sm:w-32 px-4 py-2 rounded-lg bg-[#0F676C] text-white hover:bg-[#17888D] transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      {/* Doctor list results shown directly under search button */}
      {committedSearchParams.enabled && (doctorName || location) && (
        <div className="mt-2 md:mt-2">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F676C]"></div>
            </div>
          ) : isError ? (
            <div className="text-center py-8">
              <p className="text-black-500">No Results found.</p>
            </div>
          ) : doctors.length > 0 ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  showing {startCount} of {totalItems} doctors
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    disabled={currentPage === 0}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    &lt;
                  </button>
                  <span className="px-3 py-1 text-sm font-medium">
                    {currentPage + 1}
                  </span>
                  <button
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    &gt;
                  </button>
                </div>
              </div>
              <ul
                className={cn("grid max-sm:divide-y sm:gap-2 md:grid-cols-2 lg:gap-4 xl:grid-cols-3 2xl:grid-cols-4")}
              >
                {doctors.map((doctor) => (
                  <DoctorCard
                    key={doctor.professionalId}
                    {...{
                      id: doctor.professionalId,
                      uniqueCode: doctor.professionalId,
                      professionalId: doctor.professionalId,
                      fullName: doctor.fullName,
                      specialities: doctor.speciality || [],
                      yearOfExperience: doctor.yearOfExperience || 0,
                      fee: doctor.fee || 0,
                      profilePhoto: doctor.profilePhoto ? `${doctor.profilePhoto}` : "",
                      isTeleconsultationEnable: doctor.isTeleconsultationEnable || false,
                      isInClinicConsultationEnable: doctor.isInClinicConsultationEnable || false,
                      ...(doctor.subDistrictOrCity && { subDistrictOrCity: doctor.subDistrictOrCity })
                    }}
                  />
                ))}
              </ul>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No doctors found matching your search criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchForm